import { Component, OnDestroy, OnInit, ViewChild, ElementRef, ChangeDetectorRef } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatTable, MatTableDataSource } from '@angular/material/table';
import { TranslateService } from '@ngx-translate/core';
import { UtilsService } from '../../_services/_utils.service';
import { Subject } from 'rxjs';
import { REGULAR_EXPRESSION, TAGS_MAX_COUNT, TenantConfig } from '../../app.constants';
import { LoginUserOnlineService } from '../../_services/http-api/login-user-online.service';
import { TagsService } from '../../_services/http-api/tags.service';
import { TenantConfigService } from '../../_services/http-api/tenant-config.service';
import { LangUpdateService } from '../../_services/lang-update.service';
import { SpinnerService } from '../../_services/spinner.service';
import { CommonSaveConfirmDialogComponent } from '../../common-dialog/common-save-confirm-dialog.component';
import { Tags, TagsUpsert } from '../../_objects/tags';
import { ApplicationError } from '../../_errors/application-error';
import { CommonSnackBarService } from '../../_services/common-snack-bar.service';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { CommonDeleteDialogComponent } from '../../common-dialog/common-delete-dialog.component';
import { CommonLayoutService } from '../../common-layout/common-layout.service';
import { takeUntil } from 'rxjs/operators';
import { CallToActionLabel } from '../../shared/directives/cta/cta.directive';
import { IconEnum } from '@evc/web-components';
import { Router } from '@angular/router';

@Component({
    selector: 'app-settings-tags',
    templateUrl: './settings-tags.component.html',
    styleUrls: ['./settings-tags.component.scss'],
    standalone: false
})
export class SettingsTagsComponent implements OnInit, OnDestroy {
  browserName: string;
  isAdmin = false;
  isTrans = false;
  tagListDataSource = new MatTableDataSource<string>();
  displayedTagColumns = ['tags'];
  tagsLabel = TenantConfig.DefaultConfigurationValues.LabelOfTags;
  private onDestroy$ = new Subject();

  constructor(
    private tagsService: TagsService,
    private tenantConfigService: TenantConfigService,
    private utilsService: UtilsService,
    private loginUserService: LoginUserOnlineService,
    private dialog: MatDialog,
    private oscSpinnerService: SpinnerService,
    private translate: TranslateService,
    private langService: LangUpdateService
  ) {
    this.langService.prepare(translate, this.onDestroy$);
  }

  ngOnInit(): void {
    this.browserName = this.utilsService.getBrowserName();
    this.isAdmin = this.loginUserService.isAdminRole();
    if (this.isAdmin) {
      this.loadTags();
    }
    this.setConfig();
  }

  ngOnDestroy(): void {
    this.onDestroy$.next(undefined);
    this.onDestroy$.unsubscribe();
  }

  // TenantConfig取得
  setConfig() {
    const configs = this.tenantConfigService.tenantConfig;
    if (configs) {
      this.tagsLabel = configs.find(_ => _.key === TenantConfig.LabelOfTags.KEY).value as string;
    }
  }

  loadTags() {
    this.isTrans = true;
    this.oscSpinnerService.show(true);
    this.tagsService.getTags()
      .subscribe(data => {
        this.tagListDataSource.data = data.items;
        this.oscSpinnerService.hide();
      },
      error => {
        this.isTrans = false;
        this.oscSpinnerService.hide();
        console.log(error);
      },
      () => {
        this.isTrans = false;
        this.oscSpinnerService.hide();
      }
      );
  }

  openEditTagsDialog() {
    const dialogRef = this.dialog.open(SettingsTagsEditDialogComponent, {
      width: '800px',
      maxHeight: '676px',
      autoFocus: false,
      disableClose: true,
      data: { displayedTagColumns: this.displayedTagColumns }
    });
    dialogRef.afterClosed().subscribe(result => {
      this.loadTags();
    });
  }
}

@Component({
    selector: 'app-settings-tags-edit-dialog',
    templateUrl: './settings-tags-edit-dialog.component.html',
    styleUrls: ['./settings-tags-edit-dialog.component.scss'],
    standalone: false
})
export class SettingsTagsEditDialogComponent implements OnInit, OnDestroy {
  callToActionLabel: CallToActionLabel = {
    label: this.translate.instant('commonLayout.newTag'),
    icon: IconEnum.Plus
  };

  readonly idPrefix = 'tag_';
  @ViewChild('table') table: MatTable<string>;
  displayedTagColumns = [];
  rows: string[] = [];
  tagListDataSource = new MatTableDataSource<string>();
  tags: Tags;
  preTags: string[];
  selectedTags: string;
  selectedIndex = 0;
  isTrans = false;
  isError: boolean;
  // ラベル
  tagsLabel = TenantConfig.DefaultConfigurationValues.LabelOfTags;
  private onDestroy$ = new Subject();

  constructor(
    private tagsService: TagsService,
    private snackBar: CommonSnackBarService,
    private dialog: MatDialog,
    private oscSpinnerService: SpinnerService,
    private translate: TranslateService,
    private langService: LangUpdateService,
    private utilsService: UtilsService,
    private tenantConfigService: TenantConfigService,
    private commonLayoutService: CommonLayoutService,
    private elementRef: ElementRef,
    private cd: ChangeDetectorRef,
    private router: Router
  ) {
    this.langService.prepare(translate, this.onDestroy$);
    this.displayedTagColumns = ['tags'];
    this.loadTags();
  }

  get isDisabledNewButton(): boolean {
    return !(this.tagListDataSource.data.length <= 49 && !this.tagListDataSource.data.some(item => !item));
  }

  shouldShowActionButton() {
    return this.router.url.includes('/tags');
  }

  ngOnInit() {
    this.setTagSubscribe();
    this.setConfig();
  }

  ngOnDestroy(): void {
    this.onDestroy$.next(undefined);
    this.onDestroy$.unsubscribe();
  }

  // HeaderにあるNewの押下を監視
  setTagSubscribe() {
    this.commonLayoutService
      .commonLayoutNewTagObs()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(isDisable => {
        if (isDisable === null) {
          this.addTag();
        }
      });
  }

  async canDeactivate(): Promise<boolean> {

    if (!this.isCheckChange()) {
      return true;
    }

    const dialogRef = this.dialog.open(CommonSaveConfirmDialogComponent, {
      width: '432px',
      disableClose: true,
      data: {
        isMove: true,
        isError: this.isValidationError,
        message: 'settingsTagsEditDialog.message',
        customMessage: 'settingsTagsEditDialog.customMessage',
        notSaveBtnLabel: 'settingsTagsEditDialog.notSaveBtnLabel',
      }
    });

    const result = await dialogRef.afterClosed().toPromise();

    if (result === false) {
      dialogRef.close();
      return true;
    } else if (result === '') {
      dialogRef.close();
      return false;
    } else {
      this.submit();
      return true;
    }
  }

  setConfig() {
    const configs = this.tenantConfigService.tenantConfig;
    if (configs) {
      this.tagsLabel = configs.find(_ => _.key === TenantConfig.LabelOfTags.KEY).value as string;
    }
  }

  loadTags() {
    this.oscSpinnerService.show(true);
    this.tagsService.getTags()
      .subscribe(item => {
        this.tagListDataSource.data = item.items;
        this.rows = item.items;
        this.tags = item;
        this.preTags =JSON.parse(JSON.stringify(item.items));
        this.commonLayoutService.commonLayoutNewTagEmit(this.isDisabledNewButton);
        this.oscSpinnerService.hide();
      });
  }

  selectTags(identifier: string, index: number): string {
    this.selectedIndex = index;
    return this.selectedTags = identifier;
  }

  addTag() {
    if (this.tagListDataSource.data.length < TAGS_MAX_COUNT) {

      const tempTag = this.tagListDataSource.data;
      tempTag.push('');
      this.tagListDataSource.data = tempTag;
      this.commonLayoutService.commonLayoutNewTagEmit(this.isDisabledNewButton);
      this.cd.detectChanges();

      this.elementRef.nativeElement.scrollIntoView({ behavior: 'instant', block: 'end' });
      const id = this.idPrefix + (this.tagListDataSource.data.length -1);
      const element = document.getElementById(id);
      element?.focus();
    }
  }

  dropTable(event: CdkDragDrop<string[]>) {
    const prevIndex = this.tagListDataSource.data.findIndex((d) => d === event.item.data);
    moveItemInArray(this.tagListDataSource.data, prevIndex, event.currentIndex);
    this.table.renderRows();
    this.submit();
  }

  deleteTags(index) {
    const tempTag = this.tagListDataSource.data;
    const removeElement = tempTag[index];

    if (!removeElement) {
      tempTag.splice(index, 1);
      this.tagListDataSource.data = tempTag;
      this.selectedTags = null;
      this.submit();
      this.commonLayoutService.commonLayoutNewTagEmit(this.isDisabledNewButton);
    } else {
      const dialogRef = this.dialog.open(CommonDeleteDialogComponent, {
        width: '600px',
        minWidth: '400px',
        maxHeight: '800px',
        data: { target: this.translate.instant('settingsTagsEditDialog.deleteDialogTarget', { tagsLabel: this.tagsLabel }), deleteNames: [removeElement] }
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          tempTag.splice(index, 1);
          this.tagListDataSource.data = tempTag;
          this.selectedTags = null;
          this.submit();
          this.commonLayoutService.commonLayoutNewTagEmit(this.isDisabledNewButton);
        }
      });
    }

  }

  isDisableDeleteButton(index: number) {
    const tempTag = this.tagListDataSource.data;
    const targetTag = tempTag.filter((_, i) => i !== index);
    if (!targetTag) {
      return false;
    } else if (targetTag.length === 0) {
      return true;
    } else {
      return targetTag.some(tag => tag === null || tag === undefined || tag === "");
    }
  }

  isRegularTextPattern(label: string): boolean {
    if (label === null || label === undefined) {
      throw new ApplicationError('tag is null.');
    }

    const valid = this.utilsService.CombiningRegExp([
      '^[',
      REGULAR_EXPRESSION.HALF_ALPHANUMERIC_PATTERN,
      REGULAR_EXPRESSION.KANJI_PATTERN,
      REGULAR_EXPRESSION.SYMBOL_PATTERN,
      REGULAR_EXPRESSION.OTHER_SYMBOL_PATTERN,
      ']+$'
    ]);

    return (!label.match(valid)) ? true : false;
  }

  isCheckChange(): boolean {
    const current = JSON.stringify(this.tagListDataSource.data);
    const previous = JSON.stringify(this.preTags);
    return current !== previous ? true : false;
  }

  isValidationError(): boolean {
    if (this.tagListDataSource.data.length > 50) {
      return true;
    }
    return this.tagListDataSource.data.some(item => {
      if (item.trim().length > 30 || item.trim().length < 1
        || item === '') {
        return true;
      }
      if (this.isRegularTextPattern(item)) {
        return true;
      }
    });
  }

  isSelectedTags(tagItem: string, index: number) {
    if (!this.selectedTags || !this.selectedTags || !tagItem) {
      return false;
    }
    return (this.selectedTags === tagItem && this.selectedIndex === index);
  }

  submit() {
    this.commonLayoutService.commonLayoutNewTagEmit(this.isDisabledNewButton);
    this.tagListDataSource.data = this.rows;
    if (!this.isCheckChange() || this.isValidationError()) {
      return;
    }

    this.isTrans = true;
    this.oscSpinnerService.show(true);
    if (!this.isValidationError()) {
      const tagsUpsert: TagsUpsert = new TagsUpsert();
      tagsUpsert.items = this.tagListDataSource.data;
      tagsUpsert.schemaVersion = '1';

      this.tagsService.upsertTags(tagsUpsert).subscribe(
        res => {
        },
        error => {
          this.isTrans = false;
          this.snackBar.open(this.translate.instant('settingsTagsEditDialog.submitError', { tagsLabel: this.tagsLabel }), null, null, 5000);
          this.oscSpinnerService.hide(true);
          console.log(error);
        },
        () => {
          this.isTrans = false;
          this.loadTags();
          this.oscSpinnerService.hide(true);
          this.snackBar.open(this.translate.instant('settingsTagsEditDialog.submitSuccessfully', { tagsLabel: this.tagsLabel }), null, null, 5000);
        }
      );
    } else {
      this.isTrans = false;
      this.oscSpinnerService.hide(true);
      this.snackBar.open(this.translate.instant('settingsTagsEditDialog.submitInvalid', { tagsLabel: this.tagsLabel }), null, null, 5000);
    }
  }

  /**
   * identifierに値が入力されているか必須確認を行う
   */
  requiredIdentifier(identifier: string) {
    return !identifier || identifier.trim().length < 1;
  }
}
